<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ImovelAPI from 'dashboard/api/imoveis';
import { salesPartyAddressFields } from '../composables/salesPartyAddressFields';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  propertyAddress: { type: Object, default: () => ({}) },
  canCopyPropertyAddress: { type: Boolean, default: false },
  partyKind: { type: String, default: '' },
  partyRole: { type: String, default: '' },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const address = computed(() => props.profile.address || {});
const cepError = ref('');
let requestedCep = '';
const autoFields = ['street', 'city', 'state', 'neighborhood'];
const fields = computed(() => salesPartyAddressFields(t));
const rows = computed(() => [
  fields.value.slice(0, 2),
  fields.value.slice(2, 5),
  fields.value.slice(5),
]);
const autoFieldDisabled = ref(
  Object.fromEntries(autoFields.map(field => [field, true]))
);
const roleLabel = computed(() => {
  if (props.partyRole === 'vendedor')
    return t('SALES_PARTIES.PERSONAL.SELLER_ROLE');
  if (props.partyRole === 'comprador')
    return t('SALES_PARTIES.PERSONAL.BUYER_ROLE');
  return '';
});
const title = computed(() => {
  if (props.partyKind === 'pessoa_juridica')
    return t('SALES_PARTIES.ADDRESS.COMPANY_TITLE');

  return roleLabel.value
    ? t('SALES_PARTIES.ADDRESS.PERSON_TITLE', { role: roleLabel.value })
    : t('SALES_PARTIES.ADDRESS.TITLE');
});
const update = (field, value) => emit('update', 'address', field, value);
const replaceAddress = changes =>
  emit('update', 'address', { ...address.value, ...changes });
const setAutoFieldDisabled = values => {
  autoFieldDisabled.value = Object.fromEntries(
    autoFields.map(field => [field, Boolean(values[field])])
  );
};
const copyPropertyAddress = () => {
  setAutoFieldDisabled(props.propertyAddress);
  replaceAddress(props.propertyAddress);
};
const normalizeCep = value =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
const clearLookupFields = cep =>
  replaceAddress({ cep, street: '', city: '', state: '', neighborhood: '' });
const failedLookup = cep => {
  cepError.value = t('SALES_PARTIES.ADDRESS.CEP_NOT_FOUND');
  setAutoFieldDisabled({});
  clearLookupFields(cep);
  return null;
};
const lookupCep = async value => {
  const cep = normalizeCep(value);
  requestedCep = cep;
  cepError.value = '';
  if (cep.length !== 9) {
    clearLookupFields(cep);
    return null;
  }
  try {
    const response = await ImovelAPI.normalizeAddress({ cep });
    if (requestedCep !== cep) return null;
    const result = response?.data || {};
    if (!result.address && !result.city && !result.state)
      return failedLookup(cep);
    const nextAddress = {
      cep,
      street: result.address || '',
      city: result.city || '',
      state: result.state || '',
      neighborhood: result.neighborhood || '',
    };
    setAutoFieldDisabled(nextAddress);
    replaceAddress(nextAddress);
    return null;
  } catch {
    return failedLookup(cep);
  }
};
const inputValue = field =>
  field === 'cep' ? normalizeCep(address.value[field]) : address.value[field];
const locked = field => Boolean(autoFieldDisabled.value[field]);
const numeric = field => ['cep', 'number'].includes(field);
const fieldError = field =>
  field === 'cep' ? cepError.value || props.errors[field] : props.errors[field];
const fieldFilled = field => Boolean(String(inputValue(field) || '').trim());
const referenceControlClasses = ({ filled, disabled, error }) => {
  const classes = [
    'reset-base h-8 w-full border-0 border-b bg-transparent px-0 pr-7 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0',
  ];
  if (error) {
    classes.push('border-[#D64545] text-[#D64545]');
  } else if (filled && !disabled) {
    classes.push('border-[#009F94] font-bold text-[#009F94]');
  } else {
    classes.push('border-[#A7B7BE] text-[#7F8C93]');
  }
  if (disabled) classes.push('cursor-not-allowed text-[#7F8C93]');
  return classes;
};
const handleInput = (field, value) => {
  if (field === 'cep') return lookupCep(value);
  return update(field, field === 'number' ? value.replace(/\D/g, '') : value);
};
</script>

<template>
  <section
    data-test="sales-party-address-section"
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
  >
    <div class="grid gap-[22px] max-[700px]:gap-7">
      <header>
        <h2
          data-test="sales-party-address-title"
          class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
        >
          {{ title }}
        </h2>
      </header>
      <button
        v-if="canCopyPropertyAddress"
        type="button"
        data-test="sales-party-address-copy"
        class="relative flex min-h-11 w-fit items-center gap-2 rounded-[10px] bg-white px-[15px] py-[10px] text-base font-medium leading-6 text-[#009F94] ring-1 ring-inset ring-[#009F94] max-[700px]:min-h-[78px] max-[700px]:w-full max-[700px]:justify-center"
        @click="copyPropertyAddress"
      >
        <span
          class="i-lucide-clipboard size-5 max-[700px]:absolute max-[700px]:left-[15px]"
          aria-hidden="true"
        />
        <span class="max-[700px]:max-w-[200px] max-[700px]:text-center">
          {{ t('SALES_PARTIES.ACTIONS.COPY_PROPERTY_ADDRESS') }}
        </span>
      </button>
      <div class="grid gap-8">
        <div
          v-for="row in rows"
          :key="row[0][0]"
          data-address-row
          class="grid gap-8 min-[701px]:gap-4"
          :class="
            row.length === 2
              ? 'min-[701px]:grid-cols-2'
              : 'min-[701px]:grid-cols-3'
          "
        >
          <div
            v-for="[field, label, placeholder, required] in row"
            :key="field"
            :data-address-field="field"
            class="min-w-0"
          >
            <SalesPropertyReferenceField
              :filled="fieldFilled(field)"
              :disabled="locked(field)"
              :label="label"
              :error="fieldError(field)"
              :required="required"
              :test-id="`sales-party-address-${field}`"
            >
              <input
                :value="inputValue(field)"
                :inputmode="numeric(field) ? 'numeric' : undefined"
                :maxlength="field === 'cep' ? 9 : undefined"
                :placeholder="placeholder"
                :disabled="locked(field)"
                class="appearance-none"
                :class="
                  referenceControlClasses({
                    disabled: locked(field),
                    error: fieldError(field),
                    filled: fieldFilled(field),
                  })
                "
                @input="handleInput(field, $event.target.value)"
              />
            </SalesPropertyReferenceField>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
